// DraggablePlayerWindow.jsx
'use strict';
import React, { useCallback, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import { LivestreamPlayerContext } from '../context/LivestreamPlayerContext.js';
import {
  EXCLUSIVE_VIEWER_FLOAT_Z_INDEX,
  exclusiveViewerWindowSizeStyle,
  exclusiveViewerWindowVisualStyle,
  getExclusiveViewerFloatingConfig,
  getPipWindowStyle,
} from '../resource/exclusiveViewerStyle.js';
import media from '../style/media.js';
import { color } from '../style/variables.js';

import useDraggableFloatingWindow from '../hook/useDraggableFloatingWindow.js';

/**
 * Position the streamer player window and make it draggable while it is the PiP.
 * Isolates useDraggableFloatingWindow from the class based LiveStream; the
 * player keeps its React tree and DOM container, only layout and drag switch.
 * @param {object} props
 * @param {boolean} [props.isFloating] - Whether the player is the PiP window.
 * @param {boolean} [props.shouldKeepTopGiftSpace] - Keep the mini window clear of the topGift panel.
 * @param {object} [props.playerContainerRef] - Caller ref kept pointing at this window.
 * @param {number} [props.videoWidth] - Streamer HTML video intrinsic width.
 * @param {number} [props.videoHeight] - Streamer HTML video intrinsic height.
 * @param {React.ReactNode} [props.children] - Player, watermark and mask.
 * @returns {React.ReactElement} The player window.
 * @example
 * <DraggablePlayerWindow isFloating playerContainerRef={this.playerContainerRef}>
 *   {player}
 * </DraggablePlayerWindow>
 */
const DraggablePlayerWindow = ({
  isFloating = false,
  shouldKeepTopGiftSpace = false,
  playerContainerRef = null,
  videoWidth = null,
  videoHeight = null,
  children = null,
}) => {
  const floatingConfig = useMemo(
    () =>
      getExclusiveViewerFloatingConfig({
        hasTopGift: shouldKeepTopGiftSpace,
      }),
    [shouldKeepTopGiftSpace]
  );
  const floatingWindow = useDraggableFloatingWindow({
    isEnabled: isFloating,
    ...floatingConfig,
  });

  const floatingStyle = useMemo(
    () =>
      isFloating
        ? {
            ...floatingWindow.style,
            ...getPipWindowStyle({ videoWidth, videoHeight }),
          }
        : undefined,
    [isFloating, floatingWindow.style, videoWidth, videoHeight]
  );

  // The viewer camera portals its controls into this element, so publish it on
  // the shared player context; both components sit under the same provider.
  const { setPipControlsMount } = useContext(LivestreamPlayerContext) ?? {};
  const setControlsMountElement = useCallback(
    element => setPipControlsMount?.(element),
    [setPipControlsMount]
  );

  // The caller ref and the drag hook must share the same window element.
  const setWindowElement = useCallback(
    element => {
      floatingWindow.ref.current = element;
      if (playerContainerRef) {
        playerContainerRef.current = element;
      }
    },
    [floatingWindow.ref, playerContainerRef]
  );

  return (
    <PlayerWrapper
      ref={setWindowElement}
      className={isFloating ? 'swiper-no-swiping' : undefined}
      style={floatingStyle}
      $isFloating={isFloating}
    >
      {children}
      {isFloating && <PipControlsMount ref={setControlsMountElement} />}
    </PlayerWrapper>
  );
};

DraggablePlayerWindow.propTypes = {
  isFloating: PropTypes.bool,
  shouldKeepTopGiftSpace: PropTypes.bool,
  playerContainerRef: PropTypes.object,
  videoWidth: PropTypes.number,
  videoHeight: PropTypes.number,
  children: PropTypes.node,
};

const PlayerWrapper = styled.div`
  position: absolute;
  z-index: -1;
  top: 0px;
  left: 0px;
  right: 0px;
  bottom: 0px;
  /* when exclusive viewer camera window switch to player position, player wrapper then move to mini window */
  ${({ $isFloating }) =>
    $isFloating &&
    css`
      right: auto;
      bottom: auto;
      ${exclusiveViewerWindowSizeStyle}
      ${exclusiveViewerWindowVisualStyle}
      z-index: ${EXCLUSIVE_VIEWER_FLOAT_Z_INDEX};
      overflow: hidden;
      background: ${color.neutral[0]};
      /* The doubled class outranks the player's own object-fit: contain. */
      ${media.tablet`
        && video {
          object-fit: cover;
          object-position: center;
        }
      `}
    `}
`;

// Portal target for the viewer camera mini window controls, so they share this
// window's size and drag transform.
const PipControlsMount = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
`;

export default DraggablePlayerWindow;
